import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Linkedin, Twitter, Shield, BadgeCheck, ExternalLink } from 'lucide-react';

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: 'About', path: '/about' },
    { name: 'Services', path: '/services' },
    { name: 'Mobile Units', path: '/mobile-vans' },
    { name: 'Impact', path: '/statistics' },
  ];

  const services = [
    'Cardiovascular Screening',
    'Cancer Detection',
    'Diabetes Screening',
    'Wellness Programs',
    'Healthcare Provider Management',
  ];

  const legal = [
    { name: 'HIPAA Rights', path: '/hipaa' },
    { name: 'Privacy Policy', path: '/privacy' },
    { name: 'Terms of Service', path: '/terms' },
  ];

  return (
    <footer className="bg-zinc-950 border-t border-white/10" data-testid="footer">
      <div className="container mx-auto px-6 max-w-7xl py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 lg:gap-8">
          {/* Brand Column */}
          <div className="lg:col-span-2 space-y-6">
            <Link to="/" className="inline-block" data-testid="footer-logo">
              <span className="font-bold text-xl text-white tracking-tighter font-['Outfit']">
                HealthCheck
              </span>
              <span className="font-light text-xl text-zinc-400 tracking-tight font-['Outfit']">
                Systems
              </span>
            </Link>
            <p className="text-zinc-500 text-sm leading-relaxed max-w-sm">
              Premium preventative health screening solutions for forward-thinking organizations. 
              We also provide comprehensive management services for healthcare providers nationwide.
            </p>
            <div className="flex gap-3">
              <motion.a
                href="#"
                whileHover={{ scale: 1.1, y: -2 }}
                className="w-9 h-9 rounded-full bg-zinc-900 border border-white/10 flex items-center justify-center hover:bg-emerald-500/10 hover:border-emerald-500/30 transition-all"
                data-testid="social-linkedin"
              >
                <Linkedin className="w-4 h-4 text-zinc-400" />
              </motion.a>
              <motion.a
                href="#"
                whileHover={{ scale: 1.1, y: -2 }}
                className="w-9 h-9 rounded-full bg-zinc-900 border border-white/10 flex items-center justify-center hover:bg-emerald-500/10 hover:border-emerald-500/30 transition-all"
                data-testid="social-twitter"
              >
                <Twitter className="w-4 h-4 text-zinc-400" />
              </motion.a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold text-sm text-white mb-6 font-['Outfit']">Navigation</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-zinc-500 hover:text-emerald-400 transition-colors text-sm"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(' ', '-')}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-semibold text-sm text-white mb-6 font-['Outfit']">Services</h4>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service}>
                  <span className="text-zinc-500 text-sm">{service}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-semibold text-sm text-white mb-6 font-['Outfit']">Contact</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                <div>
                  <span className="text-zinc-400 text-sm block">
                    901 S Central Expy Ste 100<br />
                    Richardson, TX 75080
                  </span>
                  <span className="text-emerald-500 text-xs mt-1 block">Main Office</span>
                </div>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                <a href="tel:+18558312075" className="text-zinc-500 hover:text-emerald-400 text-sm transition-colors">
                  (855) 831-2075
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                <a href="mailto:info@healthchecksolutions.online" className="text-zinc-500 hover:text-emerald-400 text-sm transition-colors">
                  info@healthchecksolutions.online
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* HIPAA + NPI Trust Row */}
        <div className="mt-12 grid md:grid-cols-2 gap-4">
          <div className="p-6 bg-zinc-900/50 rounded-2xl border border-white/5">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center flex-shrink-0">
                <Shield className="w-5 h-5 text-emerald-500" />
              </div>
              <div>
                <h5 className="text-white font-medium text-sm mb-1">HIPAA Compliant</h5>
                <p className="text-zinc-500 text-xs leading-relaxed">
                  HealthCheck Systems is fully compliant with HIPAA regulations. Your health information is protected
                  by federal law. <Link to="/hipaa" className="text-emerald-400 hover:underline">Learn about your rights →</Link>
                </p>
              </div>
            </div>
          </div>

          <a
            href="https://npiregistry.cms.hhs.gov/provider-view/1760394282"
            target="_blank"
            rel="noopener noreferrer"
            className="group p-6 bg-zinc-900/50 rounded-2xl border border-white/5 hover:border-emerald-500/30 hover:bg-emerald-500/5 transition-all"
            data-testid="npi-badge-footer"
          >
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center flex-shrink-0 group-hover:bg-emerald-500/20 transition-colors">
                <BadgeCheck className="w-5 h-5 text-emerald-500" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h5 className="text-white font-medium text-sm">Verified NPI Provider</h5>
                  <ExternalLink className="w-3 h-3 text-zinc-500 group-hover:text-emerald-400 transition-colors" />
                </div>
                <p className="text-zinc-500 text-xs leading-relaxed">
                  NPI: <span className="font-mono text-emerald-400" data-testid="npi-number">1760394282</span>
                  <span className="text-zinc-600"> · Verify on the CMS NPI Registry →</span>
                </p>
              </div>
            </div>
          </a>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex flex-col md:flex-row items-center gap-2 md:gap-4 text-sm">
            <p className="text-zinc-600">
              © {currentYear} HealthCheck Systems. All rights reserved.
            </p>
            <span className="hidden md:inline text-zinc-700">·</span>
            <a
              href="https://npiregistry.cms.hhs.gov/provider-view/1760394282"
              target="_blank"
              rel="noopener noreferrer"
              className="text-zinc-600 hover:text-emerald-400 transition-colors inline-flex items-center gap-1.5"
              data-testid="npi-link-bottom"
            >
              NPI <span className="font-mono text-zinc-500 group-hover:text-emerald-400">1760394282</span>
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>
          <div className="flex gap-6">
            {legal.map((item) => (
              <Link 
                key={item.path}
                to={item.path} 
                className="text-zinc-600 hover:text-zinc-400 text-sm transition-colors"
              >
                {item.name}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};
